import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader, Info, ShieldX } from 'lucide-react';
import AnimatedLogo from '../common/AnimatedLogo';
import { useAuthStore } from '../../store';
import toast from 'react-hot-toast';
import Button from '../common/Button';

interface RegisterForm {
  username: string;
  email?: string;
  password: string;
  confirmPassword: string;
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register: registerUser, isLoading } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationMode, setRegistrationMode] = useState<'open' | 'invite_only' | null>(null);
  const [modeLoading, setModeLoading] = useState(true);
  const [registrationDenied, setRegistrationDenied] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>();

  const password = watch('password');

  // Fetch registration mode on mount
  useEffect(() => {
    const fetchMode = async () => {
      try {
        const { default: apiService } = await import('../../services/api');
        const response = await apiService.getRegistrationMode();
        setRegistrationMode(response.mode as 'open' | 'invite_only');
      } catch {
        // Default to open behavior on failure (fail silently)
        setRegistrationMode('open');
      } finally {
        setModeLoading(false);
      }
    };
    fetchMode();
  }, []);

  const isInviteOnly = registrationMode === 'invite_only';

  const onSubmit = async (data: RegisterForm) => {
    try {
      await registerUser(data.username, data.password, data.email);
      toast.success('Account created successfully!');
      navigate('/dashboard');
      // Dispatch salute after navigation so the sidebar AnimatedLogo is mounted
      setTimeout(() => window.dispatchEvent(new CustomEvent('agent-salute')), 300);
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 403) {
        setRegistrationDenied(true);
        return;
      }
      const errorMessage = error.response?.data?.detail || error.message || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-night py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 shadow rounded-lg p-6 sm:p-8 mx-4 sm:mx-auto dark:bg-cap-dark">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center">
            <AnimatedLogo size={64} />
          </div>
          <h2 className="mt-6 text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">
            Create your account
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Start orchestrating AI agents today
          </p>
        </div>

        {/* Registration denied state */}
        {registrationDenied && (
          <div data-testid="registration-denied" className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <ShieldX className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">
                Registration is currently restricted
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Agent Commander is in early access. To join, you'll need an invitation
                from an administrator.
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-400">
              <p>If you believe you should have access, please check that you're
                registering with the email address your invitation was sent to.</p>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                type="button"
                onClick={() => setRegistrationDenied(false)}
                className="btn btn-primary bg-primary-600 w-full"
              >
                Try a different email
              </Button>
              <Link
                to="/login"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Already have an account? Sign in
              </Link>
            </div>
          </div>
        )}

        {/* Loading skeleton while fetching mode */}
        {!registrationDenied && modeLoading && (
          <div data-testid="registration-mode-skeleton" className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          </div>
        )}

        {/* Invite-only banner */}
        {!registrationDenied && !modeLoading && isInviteOnly && (
          <div
            data-testid="invite-only-banner"
            role="alert"
            className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3"
          >
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p className="font-medium">Registration is currently by invitation only.</p>
              <p className="mt-1">Please use the email address your invitation was sent to.</p>
            </div>
          </div>
        )}

        {/* Form */}
        {!registrationDenied && <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Username
              </label>
              <input
                id="username"
                {...register('username', {
                  required: 'Username is required',
                  minLength: {
                    value: 3,
                    message: 'Username must be at least 3 characters',
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_]+$/,
                    message: 'Username can only contain letters, numbers, and underscores',
                  },
                })}
                type="text"
                className={`input mt-1 ${errors.username ? 'border-red-300' : ''}`}
                placeholder="Choose a username"
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.username.message}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email{isInviteOnly && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                id="email"
                {...register('email', {
                  ...(isInviteOnly ? { required: 'Email is required for invitation-only registration' } : {}),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                type="email"
                required={isInviteOnly}
                className={`input mt-1 ${errors.email ? 'border-red-300' : ''}`}
                placeholder="your@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              <div className="relative mt-1">
                <input
                  id="password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className={`input pr-10 ${errors.password ? 'border-red-300' : ''}`}
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center min-w-[48px] min-h-[48px] justify-center"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Confirm Password
              </label>
              <div className="relative mt-1">
                <input
                  id="confirmPassword"
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === password || 'Passwords do not match',
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`input pr-10 ${errors.confirmPassword ? 'border-red-300' : ''}`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center min-w-[48px] min-h-[48px] justify-center"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary bg-primary-600 w-full"
          >
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Creating account...
              </>
            ) : (
              'Create account'
            )}
          </Button>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Sign in
              </Link>
            </p>
          </div>
        </form>}
      </div>
    </div>
  );
};

export default RegisterPage;
